import { useState } from "react";
import { Copy, Bookmark, Check, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ErrorService } from "@/lib/errors/ErrorService";
import {
  generateDocxContent,
  cleanContentForTitle,
  cleanContentForStorage,
  sanitizeFileName,
} from "../../utils/docxExport";
import { triggerBlobDownload } from "@/utils/blobDownload";
import { MessageFeedback } from "./MessageFeedback";
import type { MessageFeedbackState } from "@hector/shared-types";

interface MessageActionButtonsProps {
  messageContent: string;
  /**
   * Real `casediscovery_messages_v2.id` of the last assistant turn. When present,
   * the feedback thumbs are shown; undefined (e.g. a still-live exchange) hides them.
   */
  messageId?: string;
  /** Vote already cast for this message (re-lights the thumb on conversation open). */
  feedbackInitial?: MessageFeedbackState;
  onSaveToResearch?: () => void;
  className?: string;
}

/**
 * MessageActionButtons - Action buttons for assistant messages
 * Similar to ChatGPT/Gemini style: Copy, Save to Research, Retry
 */
export function MessageActionButtons({
  messageContent,
  messageId,
  feedbackInitial,
  onSaveToResearch,
  className
}: MessageActionButtonsProps) {
  const [isCopied, setIsCopied] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Export message as Word document (.docx) and trigger browser download
  const handleExportWord = async () => {
    try {
      setIsExporting(true);
      const cleanContent = cleanContentForStorage(messageContent);
      const title = cleanContentForTitle(messageContent).slice(0, 60) || "Recherche Hector";
      const blob = await generateDocxContent(title, cleanContent);
      const fileName = `${sanitizeFileName(title)}.docx`;
      triggerBlobDownload(blob, fileName);
      toast.success("Document Word téléchargé");
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'export_word' } });
    } finally {
      setIsExporting(false);
    }
  };

  // Copy message content to clipboard
  const handleCopy = async () => {
    try {
      // Clean the content before copying (remove XML tags, etc.)
      const cleanContent = cleanMessageContent(messageContent);
      await navigator.clipboard.writeText(cleanContent);
      setIsCopied(true);
      toast.success("Copié dans le presse-papiers");
      
      // Reset copied state after 2 seconds
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'copy_message' } });
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className={cn("flex items-center gap-1", className)}>
        {/* Copy Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className="h-6 w-6 p-0 text-grey-400 hover:text-grey-700 hover:bg-grey-100 rounded-md transition-colors"
            >
              {isCopied ? (
                <Check className="h-3.5 w-3.5 text-emerald-900" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            {isCopied ? "Copié !" : "Copier"}
          </TooltipContent>
        </Tooltip>

        {/* Save to Research Button — only shown when handler is provided */}
        {onSaveToResearch && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onSaveToResearch}
                aria-label="Sauvegarder dans Recherche"
                className="h-6 w-6 p-0 text-grey-400 hover:text-grey-700 hover:bg-grey-100 rounded-md transition-colors"
              >
                <Bookmark className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Sauvegarder dans Recherche
            </TooltipContent>
          </Tooltip>
        )}

        {/* Export as Word document */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExportWord}
              disabled={isExporting}
              className="h-6 w-6 p-0 text-grey-400 hover:text-grey-700 hover:bg-grey-100 rounded-md transition-colors"
            >
              <FileDown className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            Exporter en Word
          </TooltipContent>
        </Tooltip>

        {/* Feedback thumbs — only once the assistant turn is persisted (real DB id). */}
        {messageId && (
          <>
            <span className="mx-1 h-4 w-px bg-grey-200" />
            <MessageFeedback messageId={messageId} initial={feedbackInitial} />
          </>
        )}
      </div>
    </TooltipProvider>
  );
}

/**
 * Clean message content for clipboard
 * Removes XML tags, metadata, and markdown formatting, keeping only plain readable text
 */
function cleanMessageContent(content: string): string {
  if (!content) return "";
  
  let cleaned = content;
  
  // Remove THINKING sections
  cleaned = cleaned.replace(/THINKING:[\s\S]*?END_THINKING/gi, '');
  cleaned = cleaned.replace(/THINKING[\s\S]*?END_THINKING/gi, '');
  
  // Remove quick_analysis_and_decision sections
  cleaned = cleaned.replace(/<quick_analysis_and_decision>[\s\S]*?<\/quick_analysis_and_decision>/gi, '');
  
  // Extract content from response_to_user tags if present
  const responseMatch = cleaned.match(/<response_to_user>([\s\S]*?)<\/response_to_user>/i);
  if (responseMatch) {
    cleaned = responseMatch[1] ?? '';
  }

  // Remove metadata sections
  cleaned = cleaned.replace(/<sources_documents>[\s\S]*?<\/sources_documents>/gi, '');
  cleaned = cleaned.replace(/<fichiers_uploadés>[\s\S]*?<\/fichiers_uploadés>/gi, '');
  cleaned = cleaned.replace(/<sources_utilisées>[\s\S]*?<\/sources_utilisées>/gi, '');
  cleaned = cleaned.replace(/<sources_web>[\s\S]*?<\/sources_web>/gi, '');
  
  // Remove source tags but keep readable format
  cleaned = cleaned.replace(/<source[^>]*>[\s\S]*?<\/source>/gi, '');
  
  // Remove remaining XML tags
  cleaned = cleaned.replace(/<response_to_user>/gi, '');
  cleaned = cleaned.replace(/<\/response_to_user>/gi, '');
  cleaned = cleaned.replace(/<[^>]+>/g, '');
  
  // Remove markdown formatting
  // Remove headers (# ## ### etc.) but keep the text
  cleaned = cleaned.replace(/^#{1,6}\s+/gm, '');
  
  // Remove bold (**text** or __text__) but keep the text
  cleaned = cleaned.replace(/\*\*([^*]+)\*\*/g, '$1');
  cleaned = cleaned.replace(/__([^_]+)__/g, '$1');
  
  // Remove italic (*text* or _text_) but keep the text
  cleaned = cleaned.replace(/\*([^*]+)\*/g, '$1');
  cleaned = cleaned.replace(/_([^_]+)_/g, '$1');
  
  // Remove strikethrough (~~text~~) but keep the text
  cleaned = cleaned.replace(/~~([^~]+)~~/g, '$1');
  
  // Remove inline code (`code`) but keep the text
  cleaned = cleaned.replace(/`([^`]+)`/g, '$1');
  
  // Remove code blocks (```code```) but keep the text
  cleaned = cleaned.replace(/```[\s\S]*?```/g, (match) => {
    return match.replace(/```\w*\n?/g, '').replace(/```/g, '');
  });
  
  // Remove blockquotes (> text) but keep the text
  cleaned = cleaned.replace(/^>\s+/gm, '');
  
  // Remove horizontal rules
  cleaned = cleaned.replace(/^[-*_]{3,}\s*$/gm, '');
  
  // Remove link formatting [text](url) - keep text
  cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  
  // Remove image formatting ![alt](url)
  cleaned = cleaned.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1');
  
  // Clean bullet points (- or *) - convert to plain text with dash
  cleaned = cleaned.replace(/^[\s]*[-*+]\s+/gm, '- ');
  
  // Clean numbered lists - keep as is
  
  // Clean up whitespace
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  cleaned = cleaned.trim();
  
  return cleaned;
}

